import { Controller } from "stimulus"

export default class extends Controller {
  static values = { template: String }

  apply(event) {
    event.stopPropagation()
    event.preventDefault()

    const template = this.templateValue
    let content = this.templateContent(template)
    if (content === undefined || content === null) return

    const tiptap = window.tiptapEditor
    const editor = tiptap ? null : document.querySelector(".editable, .medium-editor-element, [contenteditable=true]")
    if (!tiptap && !editor) return

    // Only apply if document is empty or user confirms
    const text = tiptap ? tiptap.state.doc.textContent.trim() : editor.innerText.trim()
    if (text.length > 0) {
      if (!confirm("This will replace your current content with the template. Continue?")) return
    }

    // Extract title from the template (first h1)
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = content
    const h1 = tempDiv.querySelector("h1")
    if (h1) {
      const titleField = document.querySelector(".post-title, textarea[name='post[title]']")
      if (titleField && !titleField.value.trim()) {
        titleField.value = h1.textContent
        titleField.dispatchEvent(new Event("input", { bubbles: true }))
        titleField.dispatchEvent(new Event("change", { bubbles: true }))
      }
      // Remove h1 from body content since it's now in the title field
      h1.remove()
      content = tempDiv.innerHTML
    }

    // Insert content into editor
    const cleanContent = content.replace(/^\s+/gm, '').trim()
    if (tiptap) {
      tiptap.commands.setContent(cleanContent)
      if (window._syncAndSaveEditor) window._syncAndSaveEditor()
    } else {
      editor.innerHTML = cleanContent
      editor.dispatchEvent(new Event("input", { bubbles: true }))
    }

    // Close the format menu
    const menu = document.getElementById("formatMenu")
    if (menu) {
      menu.classList.add("d-none")
      menu.classList.remove("d-block")
    }
  }

  templateContent(name) {
    const templates = {
      clinical_study_report: `
        <h1>Clinical Study Report</h1>
        <h2>1. Title Page</h2>
        <p>Provide the official study identification details including the study title, protocol number, phase, sponsor name, and report date.</p>
        <p>Study Title:</p>
        <p>Protocol Number:</p>
        <p>Study Phase:</p>
        <p>Sponsor:</p>
        <h2>2. Synopsis</h2>
        <p>Provide a brief overview of the study including design, objectives, methodology, and key findings. This section should be self-contained and understandable without reading the full report.</p>
        <h2>3. Introduction</h2>
        <p>Describe the therapeutic area, the investigational product, and the scientific rationale for the study. Include relevant background from preclinical and earlier clinical studies.</p>
        <h2>4. Study Objectives</h2>
        <h3>4.1 Primary Objective</h3>
        <p>State the primary objective and the hypothesis being tested.</p>
        <h3>4.2 Secondary Objectives</h3>
        <p>List additional objectives and any exploratory endpoints.</p>
        <h2>5. Investigational Plan</h2>
        <h3>5.1 Study Design</h3>
        <p>Describe the overall study design including randomization, blinding, treatment arms, dosing regimen, and duration of treatment and follow-up.</p>
        <h3>5.2 Study Population</h3>
        <p>Define the target population, key inclusion and exclusion criteria, and planned sample size with justification.</p>
        <h2>6. Study Results</h2>
        <h3>6.1 Efficacy Results</h3>
        <p>Present the primary and secondary efficacy analyses. Include statistical methods used, effect sizes, confidence intervals, and p-values.</p>
        <h3>6.2 Safety Results</h3>
        <p>Summarize adverse events, serious adverse events, deaths, and discontinuations due to adverse events. Include laboratory findings and vital signs.</p>
        <h2>7. Discussion</h2>
        <p>Interpret the study results in context of the existing literature, discuss limitations, and address any unexpected findings or protocol deviations.</p>
        <h2>8. Conclusions</h2>
        <p>Summarize the key findings and their implications for the benefit-risk profile of the investigational product.</p>
      `,
      protocol_synopsis: `
        <h1>Protocol Synopsis</h1>
        <h2>Study Title</h2>
        <p>Provide the full title of the clinical study including the investigational product, indication, and phase.</p>
        <h2>Study Phase</h2>
        <p>Specify the clinical development phase (Phase I, II, III, or IV) and the type of study (e.g., dose-finding, pivotal, confirmatory).</p>
        <h2>Study Rationale</h2>
        <p>Explain the scientific and medical rationale for conducting this study. Reference relevant preclinical data, prior clinical experience, and unmet medical need.</p>
        <h2>Study Objectives and Endpoints</h2>
        <h3>Primary Objective</h3>
        <p>State the primary objective clearly and concisely, linking it to the primary endpoint.</p>
        <h3>Primary Endpoint</h3>
        <p>Define the primary endpoint including the measurement method, timing of assessment, and clinically meaningful threshold.</p>
        <h3>Secondary Objectives</h3>
        <p>List secondary objectives and their corresponding endpoints.</p>
        <h2>Study Design</h2>
        <p>Describe the study design including type (parallel, crossover), randomization scheme, blinding methodology, treatment arms, dosing, and study duration.</p>
        <h2>Study Population</h2>
        <h3>Inclusion Criteria</h3>
        <p>List the key criteria that subjects must meet to participate in the study.</p>
        <h3>Exclusion Criteria</h3>
        <p>List conditions or factors that would disqualify a subject from participation.</p>
        <h2>Study Duration</h2>
        <p>Specify the total study duration including screening, treatment, and follow-up periods for each subject and the overall study timeline.</p>
        <h2>Statistical Considerations</h2>
        <p>Describe the sample size calculation, primary analysis method, handling of missing data, and planned interim analyses if applicable.</p>
      `,
      regulatory_submission: `
        <h1>Regulatory Submission</h1>
        <h2>1. Administrative Information</h2>
        <p>Provide all required administrative details for the submission package.</p>
        <p>Applicant:</p>
        <p>Submission Type:</p>
        <p>Date:</p>
        <h2>2. Overview</h2>
        <h3>2.1 Product Description</h3>
        <p>Describe the pharmaceutical product including active substance, formulation, route of administration, and proposed indication.</p>
        <h3>2.2 Regulatory History</h3>
        <p>Summarize prior submissions, regulatory interactions, and any granted designations (e.g., orphan drug, breakthrough therapy, fast track).</p>
        <h2>3. Quality Documentation</h2>
        <p>Provide documentation on drug substance and drug product manufacturing, characterization, specifications, stability, and quality control procedures.</p>
        <h2>4. Nonclinical Documentation</h2>
        <p>Summarize pharmacology, pharmacokinetics, and toxicology studies. Address carcinogenicity, genotoxicity, and reproductive toxicity as applicable.</p>
        <h2>5. Clinical Documentation</h2>
        <h3>5.1 Clinical Pharmacology</h3>
        <p>Present pharmacokinetic and pharmacodynamic data including absorption, distribution, metabolism, excretion, drug interactions, and special populations.</p>
        <h3>5.2 Clinical Efficacy</h3>
        <p>Summarize evidence of efficacy from clinical trials including pivotal study results, dose-response data, and relevant subgroup analyses.</p>
        <h3>5.3 Clinical Safety</h3>
        <p>Provide an integrated safety summary across all clinical studies including adverse events, laboratory findings, and any identified or potential risks.</p>
        <h2>6. Risk-Benefit Assessment</h2>
        <p>Present a balanced assessment of the product's benefits relative to its risks for the proposed indication and patient population.</p>
      `,
      meeting_minutes: `
        <h1>Meeting Minutes</h1>
        <p><strong>Date:</strong> </p>
        <p><strong>Attendees:</strong> </p>
        <p><strong>Meeting Purpose:</strong> </p>
        <h2>Agenda</h2>
        <p>List the topics discussed in order, with time allocated to each item.</p>
        <ol>
          <li></li>
          <li></li>
          <li></li>
        </ol>
        <h2>Discussion</h2>
        <p>Capture the key points raised during each agenda item, including differing viewpoints, questions asked, and information shared.</p>
        <h2>Decisions</h2>
        <p>Document all decisions reached during the meeting, including rationale and any conditions or caveats.</p>
        <ul>
          <li></li>
        </ul>
        <h2>Action Items</h2>
        <p>Record each action item with a clear owner and due date. Be specific about deliverables expected.</p>
        <table>
          <tr><th>Action</th><th>Owner</th><th>Due Date</th></tr>
          <tr><td></td><td></td><td></td></tr>
          <tr><td></td><td></td><td></td></tr>
        </table>
        <h2>Next Meeting</h2>
        <p>Confirm the date, time, location, and preliminary agenda items for the next meeting.</p>
      `,
      business_proposal: `
        <h1>Business Proposal</h1>
        <h2>Executive Summary</h2>
        <p>Provide a brief overview of the proposal, including the problem you are addressing, your proposed solution, and the expected outcomes.</p>
        <h2>Background</h2>
        <p>Describe the context and circumstances that led to this proposal. Include relevant industry trends, market conditions, or organizational needs.</p>
        <h2>Problem Statement</h2>
        <p>Clearly define the problem or opportunity being addressed. Use data or evidence to demonstrate the significance and urgency.</p>
        <h2>Proposed Solution</h2>
        <p>Detail your recommended approach, methodology, and deliverables. Explain why this solution is the best fit for the stated problem.</p>
        <h3>Scope of Work</h3>
        <p>Define the boundaries of the project, including what is and is not included.</p>
        <h3>Deliverables</h3>
        <ul>
          <li></li>
          <li></li>
          <li></li>
        </ul>
        <h2>Timeline</h2>
        <table>
          <tr><th>Phase</th><th>Description</th><th>Duration</th></tr>
          <tr><td>Phase 1</td><td></td><td></td></tr>
          <tr><td>Phase 2</td><td></td><td></td></tr>
          <tr><td>Phase 3</td><td></td><td></td></tr>
        </table>
        <h2>Budget</h2>
        <p>Provide a breakdown of costs associated with the proposed solution.</p>
        <table>
          <tr><th>Item</th><th>Cost</th></tr>
          <tr><td></td><td></td></tr>
          <tr><td></td><td></td></tr>
          <tr><td><strong>Total</strong></td><td><strong></strong></td></tr>
        </table>
        <h2>Team</h2>
        <p>Introduce the key people who will be involved and their relevant experience.</p>
        <h2>Next Steps</h2>
        <p>Outline the immediate actions needed to move forward, including approval requirements and kickoff logistics.</p>
      `,
      project_brief: `
        <h1>Project Brief</h1>
        <h2>Project Overview</h2>
        <p>Summarize the project in 2-3 sentences. What is being done, why, and for whom?</p>
        <h2>Objectives</h2>
        <p>Define the specific, measurable outcomes this project aims to achieve.</p>
        <ul>
          <li></li>
          <li></li>
          <li></li>
        </ul>
        <h2>Scope</h2>
        <h3>In Scope</h3>
        <ul>
          <li></li>
          <li></li>
        </ul>
        <h3>Out of Scope</h3>
        <ul>
          <li></li>
          <li></li>
        </ul>
        <h2>Target Audience</h2>
        <p>Describe the primary audience or end users this project serves.</p>
        <h2>Key Deliverables</h2>
        <table>
          <tr><th>Deliverable</th><th>Description</th><th>Due Date</th></tr>
          <tr><td></td><td></td><td></td></tr>
          <tr><td></td><td></td><td></td></tr>
        </table>
        <h2>Stakeholders</h2>
        <table>
          <tr><th>Name</th><th>Role</th><th>Responsibility</th></tr>
          <tr><td></td><td></td><td></td></tr>
          <tr><td></td><td></td><td></td></tr>
        </table>
        <h2>Success Criteria</h2>
        <p>Define how success will be measured at the completion of this project.</p>
        <h2>Risks and Mitigation</h2>
        <table>
          <tr><th>Risk</th><th>Impact</th><th>Mitigation</th></tr>
          <tr><td></td><td></td><td></td></tr>
        </table>
        <h2>Timeline</h2>
        <p>Provide a high-level schedule with key milestones and target dates.</p>
      `,
      case_study: `
        <h1>Case Study</h1>
        <h2>Overview</h2>
        <p>Provide a brief summary of the case, including the client or subject, industry, and the key outcome achieved.</p>
        <h2>The Challenge</h2>
        <p>Describe the problem, pain point, or opportunity the client faced. Include context about their situation, constraints, and what they had tried before.</p>
        <h2>The Solution</h2>
        <p>Explain the approach taken to address the challenge. Detail the strategy, tools, methods, or services used and why they were selected.</p>
        <h3>Implementation</h3>
        <p>Walk through the key steps of the implementation, including timeline, team involvement, and any notable decisions made along the way.</p>
        <h2>Results</h2>
        <p>Present the outcomes with specific metrics, data points, or qualitative improvements. Use concrete numbers wherever possible.</p>
        <table>
          <tr><th>Metric</th><th>Before</th><th>After</th><th>Change</th></tr>
          <tr><td></td><td></td><td></td><td></td></tr>
          <tr><td></td><td></td><td></td><td></td></tr>
        </table>
        <h2>Key Takeaways</h2>
        <p>Summarize the lessons learned and insights that are applicable to similar situations.</p>
        <ul>
          <li></li>
          <li></li>
          <li></li>
        </ul>
        <blockquote><p>"Add a client testimonial or quote here if available."</p></blockquote>
      `,
      research_report: `
        <h1>Research Report</h1>
        <h2>Abstract</h2>
        <p>Provide a concise summary of the research question, methodology, key findings, and conclusions.</p>
        <h2>1. Introduction</h2>
        <p>Introduce the research topic, its significance, and the questions or hypotheses being investigated. Provide relevant background and context.</p>
        <h2>2. Literature Review</h2>
        <p>Summarize existing research and publications relevant to this topic. Identify gaps in current knowledge that this research addresses.</p>
        <h2>3. Methodology</h2>
        <h3>3.1 Research Design</h3>
        <p>Describe the overall approach (qualitative, quantitative, mixed methods) and justify the choice.</p>
        <h3>3.2 Data Collection</h3>
        <p>Detail the methods used to gather data, including sources, tools, sample size, and timeframe.</p>
        <h3>3.3 Data Analysis</h3>
        <p>Explain the analytical methods and tools used to process and interpret the data.</p>
        <h2>4. Findings</h2>
        <p>Present the results of the research in a clear and organized manner. Use tables, charts, or other visual aids where appropriate.</p>
        <h2>5. Discussion</h2>
        <p>Interpret the findings in relation to the research questions and existing literature. Discuss implications, limitations, and areas for further investigation.</p>
        <h2>6. Conclusions</h2>
        <p>Summarize the key findings and their significance. Provide actionable recommendations based on the research.</p>
        <h2>7. References</h2>
        <p>List all sources cited in this report.</p>
      `,
      sop: `
        <h1>Standard Operating Procedure</h1>
        <p><strong>SOP Number:</strong> </p>
        <p><strong>Effective Date:</strong> </p>
        <p><strong>Version:</strong> 1.0</p>
        <p><strong>Author:</strong> </p>
        <p><strong>Approved By:</strong> </p>
        <h2>1. Purpose</h2>
        <p>Describe the objective of this procedure and why it exists.</p>
        <h2>2. Scope</h2>
        <p>Define who this procedure applies to and under what circumstances it should be followed.</p>
        <h2>3. Definitions</h2>
        <table>
          <tr><th>Term</th><th>Definition</th></tr>
          <tr><td></td><td></td></tr>
          <tr><td></td><td></td></tr>
        </table>
        <h2>4. Responsibilities</h2>
        <table>
          <tr><th>Role</th><th>Responsibility</th></tr>
          <tr><td></td><td></td></tr>
          <tr><td></td><td></td></tr>
        </table>
        <h2>5. Procedure</h2>
        <h3>5.1 Preparation</h3>
        <p>List any materials, tools, or prerequisites needed before starting.</p>
        <h3>5.2 Steps</h3>
        <ol>
          <li><strong>Step 1:</strong> </li>
          <li><strong>Step 2:</strong> </li>
          <li><strong>Step 3:</strong> </li>
          <li><strong>Step 4:</strong> </li>
          <li><strong>Step 5:</strong> </li>
        </ol>
        <h3>5.3 Verification</h3>
        <p>Describe how to verify that the procedure was completed correctly.</p>
        <h2>6. Exceptions</h2>
        <p>Document any known exceptions to this procedure and how they should be handled.</p>
        <h2>7. Revision History</h2>
        <table>
          <tr><th>Version</th><th>Date</th><th>Author</th><th>Changes</th></tr>
          <tr><td>1.0</td><td></td><td></td><td>Initial version</td></tr>
        </table>
      `,
      newsletter: `
        <h1>Newsletter Title</h1>
        <p><em>Edition date or number</em></p>
        <h2>Welcome</h2>
        <p>Open with a brief greeting and set the tone. Preview what readers will find in this edition.</p>
        <h2>Featured Story</h2>
        <p>Your lead story or announcement goes here. This should be the most important or interesting item and should be substantial enough to stand on its own.</p>
        <h2>News & Updates</h2>
        <h3>Update 1</h3>
        <p>Brief update on a relevant topic, product, or event.</p>
        <h3>Update 2</h3>
        <p>Brief update on a relevant topic, product, or event.</p>
        <h2>Tips & Resources</h2>
        <p>Share a useful tip, how-to, or curated resource that provides value to your audience.</p>
        <ul>
          <li></li>
          <li></li>
          <li></li>
        </ul>
        <h2>Upcoming Events</h2>
        <table>
          <tr><th>Event</th><th>Date</th><th>Details</th></tr>
          <tr><td></td><td></td><td></td></tr>
          <tr><td></td><td></td><td></td></tr>
        </table>
        <h2>Closing</h2>
        <p>Wrap up with a call to action, a forward-looking statement, or a personal note from the team.</p>
      `,
      blank: ""
    }
    return templates[name]
  }
}
